/**
 * Sending a subcontractor their packet (ADR 0279).
 *
 * Jonathon, 2026-10-09: "we will email it to them, they will fill it out, sign
 * it and it will automatically be sent back. needs to be a spanish vs english
 * version". This is the email that carries the link, in either language, and
 * what the office sees about packets already sent.
 *
 * Where the certificate of insurance goes is said in this letter and again on
 * the page the link opens, which is drawn by the `crew-packet` Edge Function.
 * The function cannot import this package, so it carries its own copy of the
 * two constants below; `supabase/test/crew-packet-match.test.ts` fails if the
 * two ever disagree.
 */

export type CrewPacketLanguage = "en" | "es";

/** Where a signed packet is reported, and where the certificate is sent. */
export const CREW_PACKET_RETURNS_TO = "jonathon@copperridge-roofing.com";

/** The certificate holder's address, as Jonathon gave it on 2026-10-09. */
export const CERTIFICATE_HOLDER_ADDRESS = "626 Valleyview Dr, Pelham, AL 35124";

/** `/sub/<token>`, the page a packet link opens. */
export function crewPacketLink(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, "")}/sub/${token}`;
}

export interface CrewPacketLetterInput {
  language: CrewPacketLanguage;
  company: string;
  /** Who to greet. The crew's contact, else nobody by name. */
  contactName: string | null | undefined;
  link: string;
  senderName: string | null | undefined;
}

/** The first word of a name, or null when there is no usable name. */
function firstName(name: string | null | undefined): string | null {
  const first = (name ?? "").trim().split(/\s+/)[0];
  return first ? first : null;
}

/**
 * The email that sends the link.
 *
 * Plain text, like every letter in Roof Board, so the rep can change a word
 * before it goes. It says what the packet is, how long it takes, and the one
 * thing the subcontractor has to do outside it: ask their insurance agent for
 * the certificate.
 */
export function crewPacketLetter(input: CrewPacketLetterInput): {
  subject: string;
  body: string;
} {
  const name = firstName(input.contactName);
  const sender = (input.senderName ?? "").trim();
  if (input.language === "es") {
    return {
      subject: `Su paquete de subcontratista — ${input.company}`,
      body: [
        name ? `Hola ${name}:` : "Hola:",
        "",
        "Antes de empezar a trabajar con nosotros, por favor llene y firme su paquete de subcontratista: su formulario W-9, la cuenta bancaria donde le pagamos, sus seguros y nuestro contrato de subcontratista. Toma unos diez minutos en su teléfono, y nos llega directamente cuando lo firme.",
        "",
        input.link,
        "",
        `Por favor pida también a su agente de seguros que envíe su certificado de seguro por correo a ${CREW_PACKET_RETURNS_TO}, con ${input.company}, ${CERTIFICATE_HOLDER_ADDRESS} como titular del certificado.`,
        "",
        "El enlace funciona durante 30 días.",
        "",
        "Gracias,",
        sender || input.company,
      ].join("\n"),
    };
  }
  return {
    subject: `Your subcontractor packet — ${input.company}`,
    body: [
      name ? `Hi ${name},` : "Hi,",
      "",
      "Before you start work with us, please fill in and sign your subcontractor packet: your W-9, the bank account we pay into, your insurance, and our subcontractor agreement. It takes about ten minutes on your phone, and it comes straight back to us when you sign.",
      "",
      input.link,
      "",
      `Please also ask your insurance agent to email your certificate of insurance to ${CREW_PACKET_RETURNS_TO}, with ${input.company}, ${CERTIFICATE_HOLDER_ADDRESS} as the certificate holder.`,
      "",
      "The link works for 30 days.",
      "",
      "Thank you,",
      sender || input.company,
    ].join("\n"),
  };
}

/** A packet as the office sees it in the list of ones already sent. */
export interface CrewPacketRow {
  created_at: string;
  expires_at: string;
  opened_at?: string | null;
  signed_at?: string | null;
  /**
   * When the letter carrying the link went. Null is a link that was made and
   * never sent; left out, the row is taken as sent.
   */
  sent_at?: string | null;
}

export type CrewPacketStatus =
  "signed" | "opened" | "sent" | "unsent" | "expired";

/**
 * Where a packet stands.
 *
 * Signed outranks everything, including a link that has since run out: a
 * signed packet is filed whatever its link now says.
 */
export function crewPacketStatus(
  row: CrewPacketRow,
  now: Date,
): CrewPacketStatus {
  if (row.signed_at) return "signed";
  if (new Date(row.expires_at) <= now) return "expired";
  if (row.opened_at) return "opened";
  // Opened outranks unsent: whoever opened it was handed the link somehow.
  if (row.sent_at === null) return "unsent";
  return "sent";
}

export const CREW_PACKET_STATUS_LABELS: Record<CrewPacketStatus, string> = {
  signed: "Signed",
  opened: "Opened, not signed",
  sent: "Sent, not opened",
  unsent: "Link made, not sent",
  expired: "Expired unsigned",
};
